import React from "react";
import { useTranslation } from "react-i18next";
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { cn } from "@/lib/utils";

export type DataPoint = {
  timestamp: number;
  [key: string]: number | string;
};

type ChartProps = {
  data: DataPoint[];
  title: string;
  textCenter?: boolean;
  referenceLineAt0?: boolean;
  showSettlementAsDotted?: boolean;
};

export function MultiLine({ data, title, textCenter, referenceLineAt0, showSettlementAsDotted }: ChartProps) {
  const { t } = useTranslation();
  const periods = Array.from(new Set(data.flatMap(Object.keys))).filter(
    (key) => key !== "timestamp" && !key.endsWith("_source"),
  );

  const uniquePeriods = [...new Set(periods)];

  const chartConfig = periods.reduce((acc, period, index) => {
    acc[period] = {
      label: period,
      color: `hsl(var(--chart-${index + 1}))`,
    };
    return acc;
  }, {} as ChartConfig);

  return (
    <Card>
      <CardHeader>
        <CardTitle className={cn("text-xl", textCenter ? "text-center" : "")}>{t(title)}</CardTitle>
      </CardHeader>
      <CardContent className="w-full">
        <ChartContainer config={chartConfig} className="h-48 w-full">
          <LineChart
            data={data}
            margin={{
              top: 5,
              right: 10,
              left: 10,
              bottom: 5,
            }}
            width={100}
            height={100}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="timestamp"
              tickFormatter={(unixTime) => new Date(unixTime).toISOString().split("T")[0]}
              tickLine={false}
              axisLine={false}
              tickMargin={2}
            />
            <YAxis />
            {referenceLineAt0 && <ReferenceLine y={0} stroke="purple" />}
            <Legend
              payload={uniquePeriods.map((period) => ({
                value: period,
                type: "line",
                color: `var(--color-${period})`,
              }))}
            />
            <ChartTooltip
              labelFormatter={(v) => new Date(v).toISOString().split("T")[0]}
              formatter={(value) => Number(value).toFixed(2)}
            />

            {periods.map((period) =>
              showSettlementAsDotted ? (
                <React.Fragment key={period}>
                  <Line
                    key={`${period}-realistic`}
                    type="monotone"
                    dataKey={(entry) => (entry[`${period}_source`] === "realistic" ? entry[period] : null)}
                    stroke={`var(--color-${period})`}
                    strokeWidth={2}
                    dot={false}
                    name={period}
                  />
                  <Line
                    key={`${period}-settlement`}
                    type="monotone"
                    dataKey={(entry) => (entry[`${period}_source`] !== "realistic" ? entry[period] : null)}
                    stroke={`var(--color-${period})`}
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="5 5"
                    name={period}
                    label={false}
                  />
                </React.Fragment>
              ) : (
                <Line
                  key={period}
                  type="monotone"
                  dataKey={period}
                  stroke={`var(--color-${period})`}
                  strokeWidth={2}
                  dot={false}
                  name={period}
                />
              ),
            )}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
